import React, { useState } from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { IntegratedErps } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantIntegrationDTO } from '@recurrency/core-api-schema/dist/integrations/tenantIntegrationDTO';
import { Alert, Form, Input, message } from 'antd';

import { Button } from 'components/Button';
import { InputFormItem } from 'components/FormItems';
import { Modal } from 'components/Modal';
import { ConfirmModal } from 'components/Modal/ConfirmModal';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';

interface CredentialsFormValues {
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
}

export function UpdateCredentialsModal({
  integration,
  onClose,
  onSuccess,
}: {
  integration: TenantIntegrationDTO;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { activeTenant } = useGlobalApp();
  const [form] = Form.useForm<CredentialsFormValues>();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      // Validate form fields
      const values = await form.validateFields();

      // Show confirmation modal
      const confirmed = await showAsyncModal(ConfirmModal, {
        title: 'Confirm Credential Update',
        content: (
          <div>
            <Alert
              type="warning"
              message="This action will update your database credentials"
              description={
                <>
                  <p>Updating credentials may temporarily interrupt data synchronization.</p>
                  <ul>
                    <li>Ongoing sync operations may be affected</li>
                    <li>New credentials will be used for all future connections</li>
                    <li>Old credentials will no longer work</li>
                  </ul>
                  <p>
                    <strong>Are you sure you want to proceed?</strong>
                  </p>
                </>
              }
              showIcon
            />
          </div>
        ),
        okText: 'Update Credentials',
        okType: 'primary',
        cancelText: 'Cancel',
        width: 600,
      });

      if (!confirmed) {
        return;
      }

      setIsLoading(true);

      // Update credentials
      await coreApiFetch(schemas.integrations.patchUpdatePartialIntegration, {
        pathParams: {
          tenantId: activeTenant.id,
          integrationId: integration.id,
        },
        bodyParams: {
          secrets: {
            host: values.host,
            port: parseInt(values.port, 10),
            database: values.database,
            username: values.username,
            password: values.password,
          },
        },
      });

      message.success('Credentials updated successfully!');
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error: any) {
      // Handle validation errors gracefully
      if (error.errorFields) {
        // Ant Design form validation error - don't show error message, form will show field errors
        return;
      }
      // Handle other errors
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      visible
      width="600px"
      title={
        activeTenant.erpType === IntegratedErps.NETSUITE ? 'Update NetSuite Credentials' : 'Update Database Credentials'
      }
      onCancel={onClose}
      centered
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={handleSubmit}>
          Update Credentials
        </Button>,
      ]}
    >
      <Alert
        type="info"
        message="For security reasons, current credentials are not displayed. Please enter new credentials below."
        style={{ marginBottom: 16 }}
        showIcon
      />
      <Form form={form} layout="vertical">
        {activeTenant.erpType === IntegratedErps.NETSUITE && (
          <>
            <InputFormItem
              name={['secrets', 'database']}
              label="NetSuite Account ID"
              rules={[{ required: false, message: 'Please add your NetSuite Account ID.' }]}
              placeholder="e.g., 1234567"
            />
            <InputFormItem
              name={['secrets', 'username']}
              label="Token ID"
              rules={[{ required: false, message: 'Please add your Token ID.' }]}
              placeholder="Enter your NetSuite Token ID"
            />
            <Form.Item
              label="Token Secret"
              name={['secrets', 'password']}
              rules={[{ required: false, message: 'Please add your Token Secret.' }]}
            >
              <Input.Password
                className={css`
                  height: 40px;
                  border-radius: 8px;
                `}
                placeholder="Enter your NetSuite Token Secret"
              />
            </Form.Item>
          </>
        )}
        {activeTenant.erpType === IntegratedErps.P21 && (
          <>
            <InputFormItem
              name="host"
              label="Database Host"
              placeholder="e.g., db.example.com"
              rules={[
                { required: true, message: 'Database host is required' },
                { type: 'string', min: 3, message: 'Host must be at least 3 characters' },
              ]}
            />
            <InputFormItem
              name="port"
              label="Port"
              type="number"
              placeholder="e.g., 1433 for MSSQL, 30015 for HANA"
              rules={[
                { required: true, message: 'Port is required' },
                {
                  pattern: /^[0-9]+$/,
                  message: 'Port must be a valid number',
                },
                {
                  validator: (_: unknown, value: string) => {
                    const port = parseInt(value, 10);
                    if (port < 1 || port > 65535) {
                      return Promise.reject(new Error('Port must be between 1-65535'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
            <InputFormItem
              name="database"
              label="Database Name"
              placeholder="e.g., production_db"
              rules={[{ required: true, message: 'Database name is required' }]}
            />
            <InputFormItem
              name="username"
              label="Username"
              placeholder="Database user"
              rules={[{ required: true, message: 'Username is required' }]}
            />
            <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Password is required' }]}>
              <Input.Password placeholder="Enter new password" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}
